import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild, OnChanges } from '@angular/core';
import { TRowActionEvent, TRowActionListEvent, TOnRowAddSave } from './models';
import {
  TTableInfo,
  TTableActionTypes,
  TTableTreeModel,
  TUserTextInputRowEditInfo,
  TTableActionListTypes,
  TTableRowNode,
  TOnTableEditEmit,
  TActionCellType,
  TTableCellSize,
} from './models/table.type';
import { TLiftedFormModel } from '../form/model/form.model';
import * as _ from 'lodash';

@Component({
  selector: 'ui-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent<T> implements OnChanges {
  @Input() theme: 'LIGHT' | 'DARK' = 'LIGHT';

  @Input() zTableModel: TTableTreeModel<T> = {
    tableInfo: [] as unknown as TTableTreeModel<T>['tableInfo'],
  };
  @Input() cellTextAlign: 'top' | 'middle' = 'top';
  @Input() cellSize: TTableCellSize = 'SMALL';
  @Input() showClickOnPlusMsg = false;
  @Input() omitEmptyTableTemplate = false;
  @Input() emptyTableText = 'table_model.NoResults';

  @Output() onRowAction = new EventEmitter<TRowActionEvent<T>>();
  @Output() onSelectRow = new EventEmitter<T[]>();
  @Output() onResetRow = new EventEmitter<T>();
  @Output() onTableChange = new EventEmitter<[TUserTextInputRowEditInfo<T>, boolean]>();
  @Output() onRowEditSave = new EventEmitter<[rowItem: T, payload: TLiftedFormModel['payload']]>();
  @Output() onRowDelete = new EventEmitter<[rowItem: T]>();
  @Output() onActionListItemSelection = new EventEmitter<TRowActionListEvent<T>>();

  // NOTE: Check for isValid use case
  @Output() onRowAddSave = new EventEmitter<TOnRowAddSave>();
  // @Output() onRowAddSave = new EventEmitter<[rowItem: T, payload: TLiftedFormModel['payload']]>();

  @Output() onMultiEdit = new EventEmitter<TOnTableEditEmit>();
  @Output() onButtonCLick = new EventEmitter<T>();
  @Output() onToggleChangeEmitter = new EventEmitter<{ rowItem: T; toggleChecked: boolean; rowIndex: number }>();
  @Output() onLabelledToggleChangeEmitter = new EventEmitter<{ val: string; rowIndex: number; cellIndex: number }>();
  @Output() onExternalEditableInputClickedEmitter = new EventEmitter<{ rowIndex: number; cellIndex: number }>();

  @Output() onLinkClicked = new EventEmitter<{ rowItem: T; linkClicked: string; event: Event }>();

  @ViewChild('uiTable') uiTableElm: ElementRef<HTMLDivElement> | null = null;

  //TODO[Anubhav]: getting ExpressionChangedAfterItHasBeenCheckedError for custom btn disabled
  ngOnChanges(changes: SimpleChanges): void {
    if (!_.isEqual(changes['zTableModel']?.previousValue, changes['zTableModel']?.currentValue)) {
      const selectedRows = this.zTableModel.tableInfo.rows.filter(
        (x): x is TTableRowNode<T> => x.type === 'ROW' && !!x.rowSelectionInfo?.isSelected,
      );

      this.onSelectRow.emit(selectedRows.map((v) => v.rowItem));
    }
  }

  actionCell($event: [Event, T, TTableActionTypes, TActionCellType]) {
    this.onRowAction.emit({
      event: $event[0],
      rowItem: $event[1],
      actionType: $event[2],
      cellInfo: $event[3],
    });
  }

  rowEditSave($event: [rowItem: T, payload: TLiftedFormModel['payload']]) {
    this.onRowEditSave.emit($event);
  }

  rowAddSave($event: TOnRowAddSave) {
    this.onRowAddSave.emit($event);
  }

  rowDelete($event: [rowItem: T]) {
    this.onRowDelete.emit($event);
  }

  actionListItemSelection($event: [T, TTableActionListTypes]) {
    this.onActionListItemSelection.emit({
      rowItem: $event[0],
      actionListType: $event[1],
    });
  }
  multiEdit($event: TOnTableEditEmit) {
    this.onMultiEdit.emit($event);
  }

  onButtonClick($event: T) {
    this.onButtonCLick.emit($event);
  }

  onToggleChange($event: { rowItem: T; toggleChecked: boolean; rowIndex: number }) {
    this.onToggleChangeEmitter.emit($event);
  }

  onLabelledToggleChange($event: { val: string; rowIndex: number; cellIndex: number }) {
    this.onLabelledToggleChangeEmitter.emit($event);
  }
  onExternalEditableInputClicked($event: { rowIndex: number; cellIndex: number }) {
    this.onExternalEditableInputClickedEmitter.emit($event);
  }

  onLinkClick($event: { rowItem: T; linkClicked: string; event: Event }) {
    this.onLinkClicked.emit($event);
  }

  selectedRows(selectedRows: T[]) {
    this.onSelectRow.emit(selectedRows);
  }

  resetRow(row: T) {
    this.onResetRow.emit(row);
  }

  tableChange(changesRow: TTableInfo<T>['rows']) {
    const rowTypes = changesRow.filter((v) => v.type === 'ROW') as TTableRowNode<T>[];
    const changeDRow = rowTypes.map((row) => {
      const mayBeUpdatedCells = row.cells
        .map((cell) => {
          if (cell.type === 'USER_TEXT_INPUT_CELL') {
            return { [cell.toBeMappedKey]: cell.value };
          }
          return {};
        })
        .reduce((acc, curr) => ({ ...acc, ...curr }));

      const isEditValid = !Object.values(mayBeUpdatedCells).filter((v) => !v).length;

      return { rowItem: row.rowItem, mayBeUpdatedCells, isEditValid };
    });
    const isTableEditValid = changeDRow.some((v) => !v.isEditValid);

    this.onTableChange.emit([changeDRow, isTableEditValid]);
  }
}
